import { isDefined } from "@clipboard-health/util-ts";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { type BackgroundCheckMissingDataType } from "@src/appV2/OpenShifts/ShiftAction/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { LoadingButton } from "../../components/LoadingButton";
import { invalidateShifts } from "../../invalidateShifts";
import { useCreateBookingRequest } from "../../WorkerShifts/BookingRequests/api/useCreateBookingRequest";
import { useDeleteBookingRequest } from "../../WorkerShifts/BookingRequests/api/useDeleteBookingRequest";
import { useGetBookingRequests } from "../../WorkerShifts/BookingRequests/api/useGetBookingRequests";
import { BookingRequestStatus } from "../../WorkerShifts/BookingRequests/types";
import { createBackgroundCheckUrlFromMissingDataTypes } from "../createBackgroundCheckUrlFromMissingDataTypes";

const DEBOUNCE_TIME_IN_MS = 500;

interface BookingRequestButtonProps {
  shiftId: string;
  workplaceId: string;
  missingBackgroundCheckDataTypes: BackgroundCheckMissingDataType[];
  offerId?: string;
  shiftIsAlreadyBooked: boolean;
}

export function BookingRequestButton(props: BookingRequestButtonProps) {
  const { shiftId, workplaceId, offerId, missingBackgroundCheckDataTypes, shiftIsAlreadyBooked } =
    props;
  const queryClient = useQueryClient();
  const workerId = useDefinedWorker().userId;
  const { showSuccessToast, showErrorToast } = useToast();
  const { data: bookingRequests, refetch: refetchBookingRequests } = useGetBookingRequests(
    {
      filter: {
        status: { eq: BookingRequestStatus.PENDING },
        "worker.id": { eq: workerId },
        "shift.id": { eq: shiftId },
      },
    },
    {
      enabled: true,
    }
  );

  const { mutate: createBookingRequest, isLoading: isCreatingBookingRequest } =
    useCreateBookingRequest({
      onSuccess: async (response) => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CTA_CREATE_BOOKING_REQUEST, {
          shiftId,
          offerId,
          workplaceId,
          workerId,
          bookingRequestId: response.data.id,
        });

        showSuccessToast("Shift has been requested.");
        await invalidateShifts(queryClient);
        await refetchBookingRequests();
      },
      onError: () => {
        showErrorToast("Error requesting shift!");
      },
      useErrorBoundary: false,
    });

  const { mutateAsync: deleteBookingRequest, isLoading: isDeletingBookingRequest } =
    useDeleteBookingRequest({
      onSuccess: async () => {
        showSuccessToast("Booking request canceled.");
        await invalidateShifts(queryClient);
        await refetchBookingRequests();
      },
    });

  const history = useHistory();

  const isLoading = isCreatingBookingRequest || isDeletingBookingRequest;

  const shiftHasAlreadyBeenRequested = (bookingRequests?.data?.length ?? 0) > 0;

  const bookingRequestId = bookingRequests?.data?.[0]?.id;

  const handleClick = useCallback(async () => {
    if (shiftHasAlreadyBeenRequested && isDefined(bookingRequestId)) {
      try {
        await deleteBookingRequest({ id: bookingRequestId });
        logEvent(APP_V2_USER_EVENTS.BOOKING_REQUEST_CANCELED, {
          bookingRequestId,
          workplaceId,
          workerId,
          shiftId,
        });
      } catch {
        showErrorToast("Error canceling booking request!");
      }

      return;
    }

    if (missingBackgroundCheckDataTypes.length > 0) {
      history.push(
        createBackgroundCheckUrlFromMissingDataTypes({
          queryParams: new URLSearchParams(history.location.search),
          shiftId,
          missingBackgroundCheckDataTypes,
        })
      );
      return;
    }

    createBookingRequest({ shiftId, offerId, workerId });
  }, [
    shiftHasAlreadyBeenRequested,
    bookingRequestId,
    deleteBookingRequest,
    workplaceId,
    workerId,
    shiftId,
    missingBackgroundCheckDataTypes,
    history,
    createBookingRequest,
    offerId,
    showErrorToast,
  ]);

  const debouncedHandleClick = useMemo(
    () =>
      debounce(handleClick, DEBOUNCE_TIME_IN_MS, {
        leading: true,
        trailing: false,
      }),
    [handleClick]
  );

  useEffect(() => {
    return () => {
      debouncedHandleClick.cancel();
    };
  }, [debouncedHandleClick]);

  const showCannotRequestButton =
    !isLoading && !shiftHasAlreadyBeenRequested && (!isDefined(offerId) || shiftIsAlreadyBooked);

  if (showCannotRequestButton) {
    return (
      <Button disabled variant="contained" size="large">
        Can&apos;t request
      </Button>
    );
  }

  return (
    <LoadingButton
      size="large"
      variant={shiftHasAlreadyBeenRequested ? "outlined" : "contained"}
      isLoading={isLoading}
      disabled={isLoading}
      sx={
        shiftHasAlreadyBeenRequested
          ? (theme) => ({
              color: theme.palette.error.light,
              borderColor: theme.palette.border?.primary,
            })
          : undefined
      }
      onClick={debouncedHandleClick}
    >
      {shiftHasAlreadyBeenRequested ? "Cancel request" : "Request"}
    </LoadingButton>
  );
}
